// src/Pages/BlogDetails/BlogDetails.jsx

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../Firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import "./BlogDetails.css"; // Ensure this file contains necessary styles
import Carousel from 'react-bootstrap/Carousel'; // Import Carousel from React-Bootstrap
import Spinner from 'react-bootstrap/Spinner'; // Import Spinner for loading state
import Modal from 'react-bootstrap/Modal'; // Import Modal for lightbox
import Row from 'react-bootstrap/Row'; // Import Row for grid layout
import Col from 'react-bootstrap/Col'; // Import Col for grid layout
import Card from 'react-bootstrap/Card'; // Import Card for related links
import { FiExternalLink } from "react-icons/fi"; // Import external link icon
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// Removed unused import: Image
// Removed unused function: capitalizeFirstLetter

const BlogDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const { i18n, t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  // State for Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  // State for loading
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBlogDetails = async () => {
      try {
        const docRef = doc(db, "blogs", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const blogData = docSnap.data();
          console.log("Fetched Blog Data:", JSON.stringify(blogData, null, 2)); // Enhanced Logging
          console.log("imageUrl:", blogData.imageUrl); // Verify imageUrl presence

          // Map 'imageUrl' to 'image'
          const primaryImageURL = blogData.imageUrl || null;

          // Fetch additional images URLs, if any
          const additionalImages = blogData.extraImages || [];

          const updatedBlogData = {
            ...blogData,
            image: primaryImageURL, // Correctly set 'image' to 'imageUrl'
            extraImages: additionalImages.map(img => img.url), // Extract URLs from extraImages
          };

          console.log("Updated Blog Data with Image URLs:", JSON.stringify(updatedBlogData, null, 2)); // Debugging Log
          setBlog(updatedBlogData);
        } else {
          console.error("No such document!");
          toast.error(t("noSuchDocument"));
          // Optionally, redirect to a 404 page or show a message
        }
      } catch (error) {
        console.error("Error fetching blog details:", error);
        toast.error(t("errorFetchingBlog"));
      } finally {
        setIsLoading(false);
      }
    };
    fetchBlogDetails();
  }, [id, t]);

  useEffect(() => {
    // Adjust text direction for RTL languages
    if (i18n.language === "ar") {
      document.body.dir = "rtl";
      document.body.lang = "ar";
    } else {
      document.body.dir = "ltr";
      document.body.lang = "en";
    }
  }, [i18n.language]);

  if (isLoading) {
    return (
      <div className="text-center mt-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="text-center mt-4">
        <p>{t("blogNotFound")}</p>
      </div>
    );
  }

  // Combine primary image and additional images
  const allImages = blog.image ? [blog.image, ...(blog.extraImages || [])] : blog.extraImages || [];
  console.log("All Images:", allImages); // Debugging Log

  // Helper function to get the content based on language
  const getContent = (field) => {
    if (blog[field]) {
      return blog[field][i18n.language] || blog[field]["en"] || "";
    }
    return "";
  };

  // Helper function to get the link name based on language
  const getContentLinkName = (link) => {
    return (
      link.name[i18n.language] ||
      link.name["en"] ||
      t("unnamedLink")
    );
  };

  // Handle Carousel Select
  const handleSelect = (selectedIndex, e) => {
    setActiveIndex(selectedIndex);
  };

  // Handle Image Click to Open Modal
  const handleImageClick = (img) => {
    if (img) {
      setSelectedImage(img);
      setShowModal(true);
    } else {
      toast.warn(t("imageNotAvailable"));
    }
  };

  // Handle Modal Close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage("");
  };

  return (
    <>
      {/* **Toast Container** */}
      <ToastContainer />

      <div className="container my-5">
        {/* Header Images */}
        {allImages && allImages.length > 0 ? (
          <div className="blog-details-header mb-4">
            <Carousel activeIndex={activeIndex} onSelect={handleSelect} interval={5000}>
              {allImages.map((img, index) => (
                <Carousel.Item key={index}>
                  <img
                    className="d-block w-100 carousel-image"
                    src={img}
                    alt={`Slide ${index + 1}`}
                    style={{ height: "400px", objectFit: "cover", cursor: "pointer" }}
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      toast.error(t("imageLoadError"));
                      e.target.style.display = "none"; // Hide the image if it fails to load
                      console.error(`Failed to load image: ${img}`); // Debugging Log
                    }}
                    onClick={() => handleImageClick(img)}
                  />
                  {/* Optional: Add captions */}
                  {/* <Carousel.Caption>
                    <p>{getContent("caption")}</p>
                  </Carousel.Caption> */}
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        ) : (
          <div className="text-center mb-4">
            <p>{t("noImagesAvailable")}</p>
          </div>
        )}

        {/* Blog Content */}
        <div className="blog-details-content">
          <h1 className="display-4 text-center mb-3">
            {getContent("title")}
          </h1>
          <p className="text-muted text-center mb-2">
            {blog.date
              ? new Date(blog.date).toLocaleDateString(i18n.language)
              : t("noDate")}
          </p>
          {blog.time && (
            <p className="text-center mb-4">
              <strong>{t("time")}:</strong> {blog.time}
            </p>
          )}

          <p className="lead text-justify mb-4">
            {getContent("description")}
          </p>

          <div className="mb-5" dangerouslySetInnerHTML={{ __html: getContent("content") }}></div>

          {/* Related Links */}
          {blog.links && blog.links.length > 0 && (
            <div className="blog-details-links mb-5">
              <h3 className="mb-4">{t("relatedLinks")}</h3>
              <Row xs={1} sm={2} md={3} lg={4} className="g-4">
                {blog.links.map((link, index) => (
                  <Col key={index}>
                    <Card className="h-100 shadow-sm">
                      <Card.Body>
                        <Card.Title>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-none link-title"
                          >
                            {getContentLinkName(link)} <FiExternalLink />
                          </a>
                        </Card.Title>
                        {/* Optional: Add a short description for each link if available */}
                        {link.description && (
                          <Card.Text>
                            {getContent("description") &&
                              (link.description[i18n.language] || link.description["en"] || "")}
                          </Card.Text>
                        )}
                      </Card.Body>
                      {/* Optionally, you can remove Card.Footer if not needed */}
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}

        </div>
      </div>

      {/* Modal for Enlarged Image */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{getContent("title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Enlarged Blog"
              className="img-fluid"
              style={{ maxHeight: "80vh", objectFit: "contain" }}
            />
          ) : (
            <p>{t("imageNotAvailable")}</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

// Removed unused function: capitalizeFirstLetter

export default BlogDetails;
